type UnprefixedCityTagSlug = 'special_offers' | 'group';

// Рендербот ждёт ответа до отрисовки, поэтому недоступный API не должен держать страницу пустой.
const REVIEWS_SEO_TIMEOUT_MS = 3000;
const REDIRECT_LOG_PREFIX = '[unprefixed-citytag-redirect:reviews]';

const UNPREFIXED_CITY_TAG_REVIEWS_PATH = /^\/experience\/([^/]+)\/(special_offers|group)\/reviews\/?$/;

interface CityTagReviewsSeoResponse {
  citytag?: {
    id?: number;
    tag_slug?: string;
  };
}

export interface UnprefixedCityTagMatch {
  citySlug: string;
  tagSlug: UnprefixedCityTagSlug;
}

export interface ApplyUnprefixedCityTagRedirectParams {
  pathname: string;
  /** Хвост адреса после пути: query и якорь, например `?page=2#top`. */
  urlTail: string;
  origin: string;
  getApiBaseUrl: () => string;
}

export const parseUnprefixedCityTagPath = (pathname: string): UnprefixedCityTagMatch | null => {
  const match = pathname.match(UNPREFIXED_CITY_TAG_REVIEWS_PATH);

  if (!match?.[1] || !match[2]) {
    return null;
  }

  return {
    citySlug: match[1],
    tagSlug: match[2] as UnprefixedCityTagSlug,
  };
};

export const buildPrefixedCityTagPath = (citySlug: string, cityTagId: number, tagSlug: string): string => {
  return `/experience/${citySlug}/${String(cityTagId)}-${tagSlug}/reviews/`;
};

const buildCityTagReviewsSeoUrl = (apiBaseUrl: string, citySlug: string, tagSlug: string): string => {
  const normalizedBase = apiBaseUrl.replace(/\/$/, '');

  return `${normalizedBase}/api/web/v1/seo/cities/${encodeURIComponent(citySlug)}/${encodeURIComponent(tagSlug)}/reviews/`;
};

export const fetchCityTagFromReviewsSeo = async (
  apiBaseUrl: string,
  citySlug: string,
  tagSlug: UnprefixedCityTagSlug,
): Promise<{ id: number; tagSlug: string } | null> => {
  const requestUrl = buildCityTagReviewsSeoUrl(apiBaseUrl, citySlug, tagSlug);
  const response = await fetch(requestUrl, {
    credentials: 'same-origin',
    headers: {
      'x-requested-with': 'XMLHttpRequest',
    },
    signal: AbortSignal.timeout(REVIEWS_SEO_TIMEOUT_MS),
  });

  if (!response.ok) {
    console.warn(REDIRECT_LOG_PREFIX, 'reviews seo response is not ok', {
      citySlug,
      tagSlug,
      status: response.status,
      requestUrl,
    });

    return null;
  }

  const payload = (await response.json()) as CityTagReviewsSeoResponse;
  const cityTagId = payload.citytag?.id;
  const canonicalTagSlug = payload.citytag?.tag_slug;

  if (typeof cityTagId !== 'number' || !canonicalTagSlug) {
    return null;
  }

  return { id: cityTagId, tagSlug: canonicalTagSlug };
};

export const applyUnprefixedCityTagRedirect = async ({
  pathname,
  urlTail,
  origin,
  getApiBaseUrl,
}: ApplyUnprefixedCityTagRedirectParams): Promise<boolean> => {
  const parsed = parseUnprefixedCityTagPath(pathname);

  if (!parsed) {
    return false;
  }

  console.info(REDIRECT_LOG_PREFIX, 'matched candidate path', {
    pathname,
    urlTail,
    parsed,
  });

  try {
    const apiBaseUrl = getApiBaseUrl();
    console.info(REDIRECT_LOG_PREFIX, 'resolved api base url', { apiBaseUrl });

    const cityTag = await fetchCityTagFromReviewsSeo(apiBaseUrl, parsed.citySlug, parsed.tagSlug);

    if (!cityTag) {
      console.warn(REDIRECT_LOG_PREFIX, 'city tag was not found, redirect skipped', {
        citySlug: parsed.citySlug,
        tagSlug: parsed.tagSlug,
      });

      return false;
    }

    const targetUrl = `${origin}${buildPrefixedCityTagPath(parsed.citySlug, cityTag.id, cityTag.tagSlug)}${urlTail}`;
    console.info(REDIRECT_LOG_PREFIX, 'redirecting to prefixed path', { targetUrl, cityTag });

    window.sendRedirectMeta?.({ toUrl: targetUrl, statusCode: 301 });
    window.location.replace(targetUrl);

    return true;
  } catch {
    console.warn(REDIRECT_LOG_PREFIX, 'redirect request failed', {
      citySlug: parsed.citySlug,
      tagSlug: parsed.tagSlug,
    });

    return false;
  }
};
